/**
 * API service for Content Monitoring (ContentSource, Content, AnalysisProcess)
 */

import axios from 'axios'
import { API_BASE_URL } from '../config'

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout for bulk data
})

// Add request interceptor to include JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// ============================================
// ContentSource API Functions
// ============================================

/**
 * Fetch available content source types from the backend
 * @returns {Promise<Array>} Array of source type objects with value and label
 */
export const fetchContentSourceTypes = async () => {
  try {
    const response = await api.get('/api/alerts/api/content-sources/types/')
    return response.data
  } catch (error) {
    console.error('Error fetching content source types:', error)
    throw error
  }
}

/**
 * Fetch all content sources for the current user
 * @param {Object} queryParams - Optional query parameters (name, source_type, is_active, importance)
 * @returns {Promise<Array>} Array of ContentSource objects
 */
export const fetchContentSources = async (queryParams = {}) => {
  try {
    const response = await api.get('/alerts/api/content-sources/', {
      params: queryParams
    })
    return response.data
  } catch (error) {
    console.error('Error fetching content sources:', error)
    throw error
  }
}

/**
 * Get a specific content source by ID
 * @param {number} id - ContentSource ID
 * @returns {Promise<Object>} ContentSource object
 */
export const getContentSource = async (id) => {
  try {
    const response = await api.get(`/alerts/api/content-sources/${id}/`)
    return response.data
  } catch (error) {
    console.error(`Error fetching content source ${id}:`, error)
    throw error
  }
}

/**
 * Create a new content source
 * @param {Object} data - ContentSource data
 * @param {string} data.url - URL of the content source (required)
 * @param {string} data.source_type - Type: 'youtube', 'news', 'blog', 'social', 'other' (default: 'youtube')
 * @returns {Promise<Object>} Created ContentSource object with id
 */
export const createContentSource = async (data) => {
  try {
    // Only send url and source_type - backend will set defaults for other fields
    const payload = {
      url: data.url || '',
      source_type: data.source_type || 'youtube'
    }

    const response = await api.post('/alerts/api/content-sources/', payload)
    return response.data
  } catch (error) {
    console.error('Error creating content source:', error)
    const errorMessage = error.response?.data?.detail || error.response?.data || error.message || 'Failed to create content source'
    throw {
      message: errorMessage,
      status: error.response?.status || 500,
      originalError: error
    }
  }
}

/**
 * Update an existing content source
 * @param {number} id - ContentSource ID
 * @param {Object} data - Updated data (partial update supported)
 * @returns {Promise<Object>} Updated ContentSource object
 */
export const updateContentSource = async (id, data) => {
  try {
    // Transform importance if it's a string
    const payload = { ...data }
    if (typeof payload.importance === 'string') {
      const importanceMap = {
        'low': 1,
        'medium': 2,
        'high': 3
      }
      payload.importance = importanceMap[payload.importance.toLowerCase()] || payload.importance
    }

    const response = await api.patch(`/alerts/api/content-sources/${id}/`, payload)
    return response.data
  } catch (error) {
    console.error(`Error updating content source ${id}:`, error)
    const errorMessage = error.response?.data || error.message || 'Failed to update content source'
    throw {
      message: errorMessage,
      status: error.response?.status || 500,
      originalError: error
    }
  }
}

/**
 * Delete a content source
 * @param {number} id - ContentSource ID
 * @returns {Promise<void>}
 */
export const deleteContentSource = async (id) => {
  try {
    await api.delete(`/alerts/api/content-sources/${id}/`)
  } catch (error) {
    console.error(`Error deleting content source ${id}:`, error)
    throw error
  }
}

// ============================================
// Content API Functions
// ============================================

/**
 * Fetch content items for the current user
 * @param {Object} queryParams - Query parameters (title, content_type, analysis_status)
 * @returns {Promise<Array>} Array of Content objects
 */
export const fetchContent = async (queryParams = {}) => {
  try {
    const response = await api.get('/alerts/api/content/', {
      params: queryParams
    })
    return response.data
  } catch (error) {
    console.error('Error fetching content:', error)
    throw error
  }
}

/**
 * Get a specific content item by ID
 * @param {number} id - Content ID
 * @returns {Promise<Object>} Content object
 */
export const getContent = async (id) => {
  try {
    const response = await api.get(`/alerts/api/content/${id}/`)
    return response.data
  } catch (error) {
    console.error(`Error fetching content ${id}:`, error)
    throw error
  }
}

// ============================================
// AnalysisProcess API Functions
// ============================================

/**
 * Fetch all analysis processes for the current user
 * @param {Object} queryParams - Optional query parameters (name, process_type, is_active, is_template, mention_symbols_only)
 * @returns {Promise<Array>} Array of AnalysisProcess objects
 */
export const fetchAnalysisProcesses = async (queryParams = {}) => {
  try {
    const response = await api.get('/alerts/api/analysis-processes/', {
      params: queryParams
    })
    return response.data
  } catch (error) {
    console.error('Error fetching analysis processes:', error)
    throw error
  }
}

/**
 * Get a specific analysis process by ID
 * @param {number} id - AnalysisProcess ID
 * @returns {Promise<Object>} AnalysisProcess object
 */
export const getAnalysisProcess = async (id) => {
  try {
    const response = await api.get(`/alerts/api/analysis-processes/${id}/`)
    return response.data
  } catch (error) {
    console.error(`Error fetching analysis process ${id}:`, error)
    throw error
  }
}

/**
 * Create a new analysis process
 * @param {Object} data - AnalysisProcess data
 * @param {string} data.name - Name of the analysis process (required)
 * @param {string} data.process_type - Type: 'short_content_processor', 'long_pdf_processor', 'grounded_search_processor' (required)
 * @param {string} data.base_prompt - Base prompt for the analysis (required)
 * @param {Array<number>} [data.content_sources] - Array of ContentSource IDs (required - at least one)
 * @param {Array<number>} [data.symbols] - Array of Symbol IDs (optional)
 * @param {number|null} [data.symbolgroup] - SymbolGroup ID or null (optional, alternative to symbols)
 * @returns {Promise<Object>} Created AnalysisProcess object with id
 */
export const createAnalysisProcess = async (data) => {
  try {
    // Transform ManyToMany fields to arrays of IDs
    const payload = {
      name: data.name,
      process_type: data.process_type,
      base_prompt: data.base_prompt,
      content_sources: data.content_sources?.map(cs => cs.id || cs) || data.content_sources || [],
      symbols: data.symbols?.map(s => s.id || s) || data.symbols || [],
      symbolgroup: data.symbolgroup?.id || data.symbolgroup || null
    }

    const response = await api.post('/alerts/api/analysis-processes/', payload)
    return response.data
  } catch (error) {
    console.error('Error creating analysis process:', error)
    const errorMessage = error.response?.data?.detail || error.response?.data || error.message || 'Failed to create analysis process'
    throw {
      message: errorMessage,
      status: error.response?.status || 500,
      originalError: error
    }
  }
}

/**
 * Update an existing analysis process
 * @param {number} id - AnalysisProcess ID
 * @param {Object} data - Updated data (partial update supported)
 * @returns {Promise<Object>} Updated AnalysisProcess object
 */
export const updateAnalysisProcess = async (id, data) => {
  try {
    // Transform ManyToMany fields to arrays of IDs
    const payload = {
      ...data
    }

    // Only transform if these fields are present in the update
    if (data.content_sources !== undefined) {
      payload.content_sources = data.content_sources.map(cs => cs.id || cs)
    }
    if (data.symbols !== undefined) {
      payload.symbols = data.symbols.map(s => s.id || s)
    }
    if (data.symbolgroup !== undefined) {
      payload.symbolgroup = data.symbolgroup?.id || data.symbolgroup || null
    }

    const response = await api.patch(`/alerts/api/analysis-processes/${id}/`, payload)
    return response.data
  } catch (error) {
    console.error(`Error updating analysis process ${id}:`, error)
    const errorMessage = error.response?.data || error.message || 'Failed to update analysis process'
    throw {
      message: errorMessage,
      status: error.response?.status || 500,
      originalError: error
    }
  }
}

/**
 * Delete an analysis process
 * @param {number} id - AnalysisProcess ID
 * @returns {Promise<void>}
 */
export const deleteAnalysisProcess = async (id) => {
  try {
    await api.delete(`/alerts/api/analysis-processes/${id}/`)
  } catch (error) {
    console.error(`Error deleting analysis process ${id}:`, error)
    throw error
  }
}

// ============================================
// Run Monitoring API Functions
// ============================================

/**
 * Trigger content monitoring task
 * @returns {Promise<Object>} Response with monitoring status
 */
export const runMonitoring = async () => {
  try {
    const response = await api.post('/alerts/api/content-monitoring/run/')
    return response.data
  } catch (error) {
    console.error('Error running monitoring:', error)
    const errorMessage = error.response?.data || error.message || 'Failed to run monitoring'
    throw {
      message: errorMessage,
      status: error.response?.status || 500,
      originalError: error
    }
  }
}

// ============================================
// Helper Functions
// ============================================

/**
 * Convert importance string to integer
 * @param {string} importance - 'low', 'medium', 'high'
 * @returns {number} 1, 2, or 3
 */
export const importanceStringToInt = (importance) => {
  const mapping = {
    'low': 1,
    'medium': 2,
    'high': 3
  }
  return mapping[importance?.toLowerCase()] || 2
}

/**
 * Convert importance integer to string
 * @param {number} importance - 1, 2, or 3
 * @returns {string} 'low', 'medium', 'high'
 */
export const importanceIntToString = (importance) => {
  const mapping = {
    1: 'low',
    2: 'medium',
    3: 'high'
  }
  return mapping[importance] || 'medium'
}

// ============================================
// Content Alert Creation (Single API Call)
// ============================================

/**
 * Create a complete content alert (ContentSource + AnalysisProcess + UserAlert)
 * This posts to /alerts/ endpoint which handles all creation in one request
 * 
 * @param {Object} alertData - Complete alert data
 * @param {string} alertData.type - Must be 'content'
 * @param {Object} alertData.target - Target configuration
 * @param {string} alertData.target.type - 'watchlist' or 'stock'
 * @param {Array<number>} [alertData.target.stocks] - Array of symbol IDs (if type is 'stock')
 * @param {number} [alertData.target.id] - SymbolGroup ID (if type is 'watchlist')
 * @param {Object} alertData.condition - Condition configuration
 * @param {Object} alertData.condition.source - ContentSource data
 * @param {string} alertData.condition.source.url - URL of the content source
 * @param {string} alertData.condition.source.source_type - 'youtube', 'news', 'blog', 'social', 'other'
 * @param {string} alertData.condition.base_prompt - Base prompt for analysis
 * @param {Object} alertData.notifications - Notification settings
 * @param {string} alertData.notifications.name - Alert name
 * @param {boolean} alertData.notifications.email - Email notifications
 * @param {boolean} alertData.notifications.telegram - Telegram notifications
 * @param {string} alertData.notifications.cooldown - Cooldown period (e.g., '24h', '7d')
 * @returns {Promise<Object>} Created alert data with status and message
 * 
 * @example
 * const result = await createContentAlert({
 *   type: 'content',
 *   target: {
 *     type: 'watchlist',
 *     id: 1
 *   },
 *   condition: {
 *     source: {
 *       url: 'https://www.youtube.com/@techchannel',
 *       source_type: 'youtube'
 *     },
 *     base_prompt: 'Analyze for stock mentions...'
 *   },
 *   notifications: {
 *     name: 'Tech News Alert',
 *     email: true,
 *     telegram: false,
 *     cooldown: '24h'
 *   }
 * })
 */
export const createContentAlert = async (alertData) => {
  try {
    const response = await api.post('/api/alerts/', alertData)
    return {
      data: response.data.data,
      message: response.data.message,
      status: response.status
    }
  } catch (error) {
    console.error('Error creating content alert:', error)
    // Extract error message and status from response
    const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to create content alert'
    const status = error.response?.status || 500
    throw {
      message: errorMessage,
      status: status,
      originalError: error
    }
  }
}

export default api


